import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import PageHeaderActionsBar from './ui/PageHeaderActionsBar';
import api from '../api';

const BUCKET_LABELS = {
  current: 'Current',
  '1_30': '1-30 Days',
  '31_60': '31-60 Days',
  '61_90': '61-90 Days',
  '91_plus': '91+ Days',
};

const AgedReceivablesReport = ({ initialTab = 0 }) => {
  const navigate = useNavigate();
  const { userPermissions } = useContext(AuthContext) || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    if (!userPermissions?.can_access_billing_settings) {
      setError('No access to billing features.');
      setLoading(false);
      return;
    }
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/api/billing/reports/aged-receivables/');
        setData(response.data);
      } catch (err) {
        console.error('Error fetching aged receivables:', err);
        setError(err.response?.data?.error || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [userPermissions]);

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totals = data?.totals ?? {};
  const summary = data?.summary ?? [];
  const detail = data?.detail ?? [];

  return (
    <Box>
      <PageHeaderActionsBar
        title="Aged Receivables"
        description="Outstanding balances by age bucket"
        secondaryActions={[{ label: 'Back', onClick: () => navigate('/billing/reports'), icon: <BackIcon />, variant: 'text' }]}
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Summary" />
        <Tab label="Detail" />
      </Tabs>

      {tab === 0 && (
        <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Totals</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Typography variant="body2">Current: <strong>${(totals.current ?? 0).toLocaleString()}</strong></Typography>
              <Typography variant="body2">1-30 Days: <strong>${(totals['1_30'] ?? 0).toLocaleString()}</strong></Typography>
              <Typography variant="body2">31-60 Days: <strong>${(totals['31_60'] ?? 0).toLocaleString()}</strong></Typography>
              <Typography variant="body2">61-90 Days: <strong>${(totals['61_90'] ?? 0).toLocaleString()}</strong></Typography>
              <Typography variant="body2">91+ Days: <strong>${(totals['91_plus'] ?? 0).toLocaleString()}</strong></Typography>
              <Typography variant="body2" color="primary.main">Total: <strong>${(totals.total ?? 0).toLocaleString()}</strong></Typography>
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Current</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">1-30</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">31-60</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">61-90</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">91+</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                      No outstanding receivables
                    </TableCell>
                  </TableRow>
                ) : summary.map((row) => (
                  <TableRow key={row.client_id} hover>
                    <TableCell>{row.client_name}</TableCell>
                    <TableCell align="right">${(row.current ?? 0).toLocaleString()}</TableCell>
                    <TableCell align="right">${(row['1_30'] ?? 0).toLocaleString()}</TableCell>
                    <TableCell align="right">${(row['31_60'] ?? 0).toLocaleString()}</TableCell>
                    <TableCell align="right">${(row['61_90'] ?? 0).toLocaleString()}</TableCell>
                    <TableCell align="right">${(row['91_plus'] ?? 0).toLocaleString()}</TableCell>
                    <TableCell align="right"><strong>${(row.total ?? 0).toLocaleString()}</strong></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tab === 1 && (
        <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Days Overdue</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Age Bucket</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                      No outstanding invoices
                    </TableCell>
                  </TableRow>
                ) : detail.map((row) => (
                  <TableRow key={row.invoice_id} hover>
                    <TableCell>{row.invoice_number}</TableCell>
                    <TableCell>{row.client_name}</TableCell>
                    <TableCell>{row.due_date}</TableCell>
                    <TableCell>{row.days_overdue}</TableCell>
                    <TableCell>{BUCKET_LABELS[row.age_bucket] ?? row.age_bucket}</TableCell>
                    <TableCell align="right">${(row.balance ?? 0).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default AgedReceivablesReport;
